import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useState,
  type ReactElement,
  Children,
} from 'react';
import { useIntl } from 'react-intl';

import { type WithChildren } from '@dynatrace/strato-components/core';
import { ViewIcon, ViewOffIcon } from '@dynatrace/strato-icons';

import {
  PasswordInputTooltip,
  type PasswordInputTooltipProps,
} from './PasswordInputTooltip.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { type BaseInputProps, BaseInput } from '../_base-input/BaseInput.js';
import type { FormControlRef } from '../shared-types.js';

//#region Typings
/**
 * @public
 */
export type PasswordInputProps = Pick<
  BaseInputProps,
  | 'placeholder'
  | 'autoComplete'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'onFocus'
  | 'onBlur'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<
    string,
    (value: string, event?: ChangeEvent<HTMLInputElement>) => void
  > &
  WithChildren;

type PasswordInputSlots = {
  tooltip: ReactElement<PasswordInputTooltipProps> | null;
};
//#endregion Typings

/** Iterates over child nodes to extract specific component slots. */
function getTooltipSlot(children: ReactNode) {
  const slots: PasswordInputSlots = {
    tooltip: null,
  };

  for (const child of Children.toArray(children)) {
    if (isSlot(child, PasswordInputTooltip)) {
      slots.tooltip = child;
    } else if (child !== null) {
      // do nothing if the child is null (when rendered conditionally for example) and throw a warning otherwise
      // eslint-disable-next-line no-console
      console.warn('The used child is not supported by the PasswordInput.');
    }
  }

  return slots;
}

const PasswordInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  PasswordInputProps
>((props, forwardedRef) => {
  const {
    onChange,
    name,
    readOnly = false,
    controlState,
    children,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: PasswordInputProps = props;

  const [hidden, setHidden] = useState(true);

  const onInternalChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value, event);
  };

  const { tooltip } = getTooltipSlot(children);
  const { showText, hideText, ...remainingTooltipProps } = tooltip?.props ?? {};

  const intl = useIntl();
  const showTooltipDefaultText = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'tgL6QEjmq+ArASFk',
    description:
      'Text in a tooltip shown when hovering the password input icon to show the password',
  });
  const hideTooltipDefaultText = intl.formatMessage({
    defaultMessage: 'Hide password',
    id: 'vMt3ZRHwBods5xzM',
    description:
      'Text in a tooltip shown when hovering the password input icon to hide the password',
  });

  const showTooltipText = showText ?? showTooltipDefaultText;
  const hideTooltipText = hideText ?? hideTooltipDefaultText;

  return (
    <BaseInput
      {...remainingProps}
      role="textbox"
      controlState={controlState}
      name={name}
      type={hidden ? 'password' : 'text'}
      onChange={onInternalChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      readOnly={readOnly}
      aria-disabled={ariaDisabledProp}
    >
      <BaseInput.Suffix>
        <BaseInput.Button
          onClick={() => setHidden(!hidden)}
          tooltipText={hidden ? showTooltipText : hideTooltipText}
          aria-label={hidden ? showTooltipText : hideTooltipText}
          readOnly={readOnly}
          id={remainingTooltipProps.id}
          aria-disabled={ariaDisabledProp}
        >
          {hidden ? <ViewIcon /> : <ViewOffIcon />}
        </BaseInput.Button>
      </BaseInput.Suffix>
    </BaseInput>
  );
});

/**
 * The `PasswordInput` component can be used to hide user input.
 * Always use a `PasswordInput` when your app asks for sensitive data,
 * such as a password. Users can click on the eye icon to hide or show their data.
 * @public
 */
export const PasswordInput = Object.assign(PasswordInputRoot, {
  Tooltip: PasswordInputTooltip,
});

(PasswordInput as typeof PasswordInput & { displayName: string }).displayName =
  'PasswordInput';
